import { useState, useEffect, useCallback } from 'react';
import { useBranding } from '@/contexts/BrandingContext';
import { BrandMark } from '@/components/BrandLogo';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth, api } from '@/App';
import { useI18n } from '@/i18n';
import CurrencySelector from '@/components/CurrencySelector';
import ThemeToggle from '@/components/ThemeToggle';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PropertyCard } from '@/components/property/PropertyCard';
import { PropertyFilters } from '@/components/property/PropertyFilters';
import { PropertyPagination } from '@/components/property/PropertyPagination';
import { Building2, Home, Heart, LogOut, ChevronDown } from 'lucide-react';

const PROPERTY_IMAGES = [
  "https://images.pexels.com/photos/35264654/pexels-photo-35264654.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1572177215152-32f247303126?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3RlbCUyMGludGVyaW9yJTIwcm9vbXxlbnwwfHx8fDE3NzU0ODA5MjR8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1765775635143-6462630748ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3RlbCUyMGludGVyaW9yJTIwcm9vbXxlbnwwfHx8fDE3NzU0ODA5MjR8MA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/19737883/pexels-photo-19737883.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

export default function PropertiesPage() {
  const { user, login, logout, isAuthenticated, isAdmin } = useAuth();
  const { branding: brandData } = useBranding();
  const { t, formatPrice, currency, convertToUsd, currencySymbol } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    city: searchParams.get('city') || '',
    property_type: searchParams.get('type') || '',
    min_guests: searchParams.get('guests') || '',
    country: searchParams.get('country') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    bedrooms: searchParams.get('bedrooms') || '',
    sort_by: searchParams.get('sort') || '',
    event: searchParams.get('event') || '',
    destination: searchParams.get('destination') || '',
    experience: searchParams.get('experience') || '',
    season: searchParams.get('season') || '',
  });
  const [propertyRatings, setPropertyRatings] = useState({});
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [events, setEvents] = useState([]);
  const [carouselIndexes, setCarouselIndexes] = useState({});

  const limit = 12;

  const fetchEvents = useCallback(async () => {
    try {
      const res = await api.get('/events');
      setEvents(res.data.events || []);
    } catch (_) { toast.error('Error al cargar datos'); }
  }, []);

  const fetchFavoriteIds = useCallback(async () => {
    try {
      const res = await api.get('/favorites/ids');
      setFavoriteIds(new Set(res.data.property_ids || []));
    } catch (_) { toast.error('Error al cargar datos'); }
  }, []);

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      params.append('status', 'Publicado');
      
      if (filters.search) params.append('search', filters.search);
      if (filters.city) params.append('city', filters.city);
      if (filters.property_type) params.append('property_type', filters.property_type);
      if (filters.min_guests) params.append('min_guests', filters.min_guests);
      if (filters.country) params.append('country', filters.country);
      // Convert local currency prices to USD for backend
      if (filters.min_price) params.append('min_price', convertToUsd(parseFloat(filters.min_price)).toFixed(2));
      if (filters.max_price) params.append('max_price', convertToUsd(parseFloat(filters.max_price)).toFixed(2));
      if (filters.bedrooms) params.append('bedrooms', filters.bedrooms);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      if (filters.event) params.append('event', filters.event);
      if (filters.destination) params.append('destination', filters.destination);
      if (filters.experience) params.append('experience', filters.experience);
      if (filters.season) params.append('season', filters.season);

      const response = await api.get(`/properties?${params.toString()}`);
      const props = response.data.properties || [];
      setProperties(props);
      setTotal(response.data.total || 0);
      // Fetch ratings for displayed properties
      if (props.length > 0) {
        try {
          const ids = props.map(p => p.property_id).join(',');
          const ratingsRes = await api.get(`/reviews/ratings-batch?property_ids=${ids}`);
          setPropertyRatings(ratingsRes.data.ratings || {});
        } catch (_) { toast.error('Error al cargar datos'); }
      }
    } catch (_) { toast.error('Error al cargar datos'); } finally {
      setLoading(false);
    }
  }, [page, filters, convertToUsd]); // limit is const, api/setters/toast are stable

  // Effects - placed after all useCallback definitions
  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (isAuthenticated) fetchFavoriteIds();
  }, [isAuthenticated, fetchFavoriteIds]);

  const slideCarousel = (e, propertyId, direction, totalImages) => {
    e.preventDefault();
    e.stopPropagation();
    setCarouselIndexes(prev => {
      const current = prev[propertyId] || 0;
      const next = direction === 'next'
        ? (current + 1) % totalImages
        : (current - 1 + totalImages) % totalImages;
      return { ...prev, [propertyId]: next };
    });
  };

  const toggleFavorite = async (e, propertyId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.info('Inicia sesión para guardar favoritos');
      return;
    }
    try {
      const res = await api.post(`/favorites/${propertyId}`);
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (res.data.action === 'added') next.add(propertyId);
        else next.delete(propertyId);
        return next;
      });
      toast.success(res.data.action === 'added' ? 'Agregado a favoritos' : 'Eliminado de favoritos');
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchProperties();
  };

  const clearFilters = () => {
    setFilters({ search: '', city: '', property_type: '', min_guests: '', country: '', min_price: '', max_price: '', bedrooms: '', sort_by: '', event: '', destination: '', experience: '', season: '' });
    setPage(0);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <BrandMark onDark={false} className="w-10 h-10" alt={brandData?.site_name || 'Estadía 365'} />
              <span className="font-bold text-xl text-[#1C1917]" style={{ fontFamily: 'Outfit' }}>
                Estadia 365
              </span>
            </Link>

            <div className="flex items-center space-x-4">
              <CurrencySelector className="hidden sm:flex" />
              {isAuthenticated ? (
                <div className="relative group">
                  <button className="flex items-center gap-2 border border-[#E7E5E4] rounded-full px-3 py-1.5 hover:shadow-md transition-shadow" data-testid="user-menu-btn">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[#264639] flex items-center justify-center">
                        <span className="text-white text-xs font-bold">{user?.name?.charAt(0)}</span>
                      </div>
                    )}
                    <ChevronDown className="w-3 h-3 text-[#78716C]" />
                  </button>
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-[#E7E5E4] py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <div className="px-4 py-2 border-b border-[#E7E5E4]">
                      <p className="text-sm font-medium text-[#1C1917] truncate">{user?.name}</p>
                      <p className="text-xs text-[#78716C] truncate">{user?.email}</p>
                    </div>
                    {isAdmin && (
                      <Link to="/dashboard" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#57534E] hover:bg-[#F5F4F0]">
                        <Building2 className="w-4 h-4" /> {t('nav.dashboard')}
                      </Link>
                    )}
                    <Link to="/wishlist" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#57534E] hover:bg-[#F5F4F0]">
                      <Heart className="w-4 h-4" /> {t('nav.favorites')}
                    </Link>
                    <div className="border-t border-[#E7E5E4] mt-1 pt-1">
                      <button onClick={logout} className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#C36F4D] hover:bg-[#FFF5F0] w-full">
                        <LogOut className="w-4 h-4" /> {t('nav.logout')}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <Button onClick={login} className="bg-[#264639] hover:bg-[#1C352B]">
                  {t('nav.login')}
                </Button>
              )}
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1C1917] mb-2" style={{ fontFamily: 'Outfit' }}>
              {t('properties.title')}
            </h1>
            <p className="text-[#57534E]">
              {t('properties.subtitle', { count: total })}
            </p>
          </div>

          {/* Search and Filters */}
          <PropertyFilters
            filters={filters} setFilters={setFilters} setPage={setPage}
            onSearch={handleSearch} onClearFilters={clearFilters}
            onNavigateMap={() => navigate('/map')} events={events}
            t={t} currencySymbol={currencySymbol} currency={currency}
          />

          {/* Properties Grid */}
          {loading && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={`skeleton-${i}`} className="bg-white rounded-xl overflow-hidden">
                  <div className="h-48 skeleton"></div>
                  <div className="p-4 space-y-3">
                    <div className="h-4 skeleton rounded w-1/2"></div>
                    <div className="h-5 skeleton rounded w-3/4"></div>
                    <div className="h-4 skeleton rounded w-1/3"></div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && properties.length === 0 && (
            <div className="text-center py-16">
              <Home className="w-16 h-16 text-[#78716C] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#1C1917] mb-2" style={{ fontFamily: 'Outfit' }}>
                {t('properties.noResults')}
              </h3>
              <p className="text-[#57534E] mb-4">
                Intenta ajustar los filtros de búsqueda
              </p>
              <Button onClick={clearFilters} variant="outline" className="border-[#264639] text-[#264639]">
                Limpiar filtros
              </Button>
            </div>
          )}
          {!loading && properties.length > 0 && (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-7">
                {properties.map((property, index) => (
                  <PropertyCard
                    key={property.property_id}
                    property={property}
                    index={index}
                    currentImageIdx={carouselIndexes[property.property_id] || 0}
                    onSlideCarousel={slideCarousel}
                    onToggleFavorite={toggleFavorite}
                    isFavorite={favoriteIds.has(property.property_id)}
                    rating={propertyRatings[property.property_id]}
                    formatPrice={formatPrice}
                    t={t}
                  />
                ))}
              </div>

              <PropertyPagination page={page} totalPages={totalPages} setPage={setPage} />
            </>
          )}
        </div>
      </main>
    </div>
  );
}
